import {
  ContactCapture,
  ContactCapturePublicConfig,
  ContactCaptureSettings,
  ContactCaptureType,
} from '../types';

const TEST_SESSION_PREFIX = 'bhi:contact-capture:test:';
const TEST_SESSION_TTL_MS = 15 * 60 * 1000;

export const getContactCaptureRuntimeUrl = () => {
  if (process.env.REACT_APP_CONTACT_CAPTURE_RUNTIME_URL) {
    return process.env.REACT_APP_CONTACT_CAPTURE_RUNTIME_URL;
  }
  return window.location.hostname === 'localhost'
    ? 'http://localhost:3002/v1/contact-capture.js'
    : `${window.location.origin}/widgets/v1/contact-capture.js`;
};

const escapeAttribute = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

/**
 * The snippet only carries the mode and the public id. The runtime resolves the API endpoints
 * from the origin it was served from, so one snippet works on dev, QA and production.
 */
export const getContactCaptureInstallSnippet = (type: ContactCaptureType, id?: string) => {
  const safeId = escapeAttribute(id || 'CONTACT_CAPTURE_ID');
  const runtimeUrl = escapeAttribute(getContactCaptureRuntimeUrl());

  return type === 'inline'
    ? `<div data-contact-capture="${safeId}"></div>\n<script async src="${runtimeUrl}"></script>`
    : `<script async src="${runtimeUrl}" data-contact-capture-id="${safeId}" data-contact-capture-mode="popup"></script>`;
};

export type ContactCaptureTestSession = {
  version: 1;
  expiresAt: number;
  runtimeUrl: string;
  /** The exact snippet from the Install tab, so the test page shows what customers paste. */
  installSnippet: string;
  config: ContactCapturePublicConfig;
};

type TestPageInput = {
  id: string;
  /** The id the snippet carries. Unsaved drafts have none, so the record id stands in. */
  publicId?: string;
  type: ContactCaptureType;
  settings: ContactCaptureSettings;
  consentText: string;
};

const createTestToken = () => {
  if (typeof window.crypto !== 'undefined' && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const createContactCaptureTestSession = ({
  consentText,
  id,
  publicId,
  settings,
  type,
}: TestPageInput) => {
  const token = createTestToken();
  // One id for both the snippet and the config, so the runtime accepts the config it fetches.
  const embedId = publicId || id;
  const session: ContactCaptureTestSession = {
    version: 1,
    expiresAt: Date.now() + TEST_SESSION_TTL_MS,
    runtimeUrl: getContactCaptureRuntimeUrl(),
    installSnippet: getContactCaptureInstallSnippet(type, embedId),
    config: { schemaVersion: 1, id: embedId, type, settings, consentText },
  };
  localStorage.setItem(`${TEST_SESSION_PREFIX}${token}`, JSON.stringify(session));
  return token;
};

export const openContactCaptureTestPage = (input: TestPageInput) => {
  const token = createContactCaptureTestSession(input);
  const url = new URL('/contact-capture-sandbox.html', window.location.origin);
  url.searchParams.set('testToken', token);
  window.open(url.toString(), '_blank', 'noopener,noreferrer');
  return url.toString();
};

export const getContactCaptureLiveTestUrl = (
  record: Pick<ContactCapture, 'publicId' | 'type' | 'allowedOrigins'>,
) => {
  const url = new URL('/contact-capture-embed-test.html', window.location.origin);
  url.searchParams.set('id', record.publicId);
  url.searchParams.set('mode', record.type);
  url.searchParams.set('runtime', getContactCaptureRuntimeUrl());
  (record.allowedOrigins || []).forEach(origin => url.searchParams.append('domain', origin));
  return url.toString();
};

export const testContactCaptureRecord = (record: ContactCapture) => {
  if (record.status === 'active') {
    const url = getContactCaptureLiveTestUrl(record);
    window.open(url, '_blank', 'noopener,noreferrer');
    return url;
  }
  return openContactCaptureTestPage({
  id: record.id,
  publicId: record.publicId,
  type: record.type,
  settings: record.settings,
  consentText: record.consentText,
  });
};
